/**
 * SIRS Dashboard - Enterprise Grade
 * Serious Incident Response Scheme tracking and management
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, Clock, CheckCircle, XCircle, Download, Plus,
  RefreshCw, FileText, Eye, Edit3, Calendar, Filter, Search,
  TrendingUp, TrendingDown, AlertCircle, User, Building
} from 'lucide-react';
import { sirsData } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';

const SIRSDashboard = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const selectedFacility = state.selectedFacility;
  
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  useEffect(() => {
    filterData();
  }, [incidents, filterStatus, filterSeverity, searchTerm]);

  const loadData = () => {
    setLoading(true);
    const facilityIncidents = sirsData.filter(d => d.facilityId === selectedFacility);
    setIncidents(facilityIncidents);
    setLoading(false);
  };

  const filterData = () => {
    let filtered = [...incidents];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(i => i.status.toLowerCase() === filterStatus.toLowerCase());
    }
    
    if (filterSeverity !== 'all') {
      filtered = filtered.filter(i => i.severity.toLowerCase() === filterSeverity.toLowerCase());
    }
    
    if (searchTerm) {
      filtered = filtered.filter(i =>
        i.residentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.incidentType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredIncidents(filtered);
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high':
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'under investigation': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (reportType) => {
    return reportType === 'Priority 1' ? 'bg-red-500' : 'bg-orange-500';
  };

  const stats = {
    total: incidents.length,
    thisMonth: incidents.filter(i => {
      const date = new Date(i.reportDate);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    underInvestigation: incidents.filter(i => i.status === 'Under Investigation').length,
    resolved: incidents.filter(i => i.status === 'Resolved').length,
    overdue: incidents.filter(i => {
      if (i.status === 'Under Investigation' && i.daysRemaining !== null) {
        return i.daysRemaining < 0;
      }
      return false;
    }).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <AlertTriangle className="w-8 h-8" />
              Serious Incident Response Scheme (SIRS)
            </h1>
            <p className="text-red-100 text-lg">Mandatory incident reporting and investigation tracking</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Report Incident
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-xs text-gray-500">Total Reports</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">All Time</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{stats.thisMonth}</div>
              <div className="text-xs text-gray-500">This Month</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Current Period</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-yellow-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-600">{stats.underInvestigation}</div>
              <div className="text-xs text-gray-500">Under Investigation</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Active Cases</h3>
          {stats.overdue > 0 && (
            <div className="mt-2 text-sm text-red-600 font-semibold">
              {stats.overdue} overdue
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{stats.resolved}</div>
              <div className="text-xs text-gray-500">Resolved</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Completed</h3>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by resident, incident type, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="under investigation">Under Investigation</option>
            <option value="resolved">Resolved</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Severity</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Incidents Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-600" />
              SIRS Incidents ({filteredIncidents.length})
            </h3>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-bold text-gray-700">Incident</th>
                <th className="text-left py-4 px-6 font-bold text-gray-700">Resident</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Priority</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Severity</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Status</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Due Date</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredIncidents.map((incident) => {
                const isOverdue = incident.status === 'Under Investigation' && 
                  incident.daysRemaining !== null && incident.daysRemaining < 0;
                
                return (
                  <tr key={incident.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-semibold text-gray-800">{incident.incidentType}</div>
                        <div className="text-xs text-gray-500 mt-1">{incident.incidentId}</div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{incident.residentName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${getPriorityColor(incident.reportType)}`}>
                        {incident.reportType}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(incident.severity)}`}>
                        {incident.severity}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(incident.status)}`}>
                        {incident.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {incident.daysRemaining !== null ? (
                        <div>
                          <div className={`text-sm font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-800'}`}>
                            {new Date(incident.dueDate).toLocaleDateString()}
                          </div>
                          {isOverdue ? (
                            <div className="text-xs text-red-600">Overdue</div>
                          ) : (
                            <div className="text-xs text-gray-500">{incident.daysRemaining} days left</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedIncident(incident)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => showToast('Edit incident feature coming soon', 'info')}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Incident Detail Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full mx-4 my-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedIncident.incidentType} - {selectedIncident.incidentId}
              </h2>
              <button
                onClick={() => setSelectedIncident(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Key Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="text-sm text-gray-600 mb-1">Priority</div>
                  <div className="text-lg font-bold text-red-600">{selectedIncident.reportType}</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="text-sm text-gray-600 mb-1">Severity</div>
                  <div className="text-lg font-bold text-yellow-600">{selectedIncident.severity}</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Status</div>
                  <div className="text-lg font-bold text-blue-600">{selectedIncident.status}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Report Date</div>
                  <div className="text-lg font-bold text-green-600">
                    {new Date(selectedIncident.reportDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Resident Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-600" />
                  Resident Information
                </h4>
                <div className="text-gray-700">{selectedIncident.residentName}</div>
                <div className="text-sm text-gray-500 mt-1">Resident ID: {selectedIncident.residentId}</div>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold mb-2">Incident Description</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedIncident.description}</p>
              </div>

              {/* Immediate Actions */}
              <div>
                <h4 className="font-semibold mb-2">Immediate Actions Taken</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 bg-blue-50 p-4 rounded-lg">
                  {selectedIncident.immediateActions.map((action, index) => (
                    <li key={index}>{action}</li>
                  ))}
                </ul>
              </div>

              {/* Investigation Details */}
              {selectedIncident.status === 'Under Investigation' && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold mb-2 text-yellow-800">Investigation Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Investigator:</span>
                      <span className="font-semibold ml-2">{selectedIncident.investigator}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Due Date:</span>
                      <span className="font-semibold ml-2">
                        {new Date(selectedIncident.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                    {selectedIncident.daysRemaining !== null && (
                      <div>
                        <span className="text-gray-600">Days Remaining:</span>
                        <span className={`font-semibold ml-2 ${
                          selectedIncident.daysRemaining < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {selectedIncident.daysRemaining}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Findings and Recommendations */}
              {selectedIncident.findings && (
                <div>
                  <h4 className="font-semibold mb-2">Investigation Findings</h4>
                  <p className="text-gray-700 bg-green-50 p-4 rounded-lg">{selectedIncident.findings}</p>
                </div>
              )}

              {selectedIncident.recommendations && (
                <div>
                  <h4 className="font-semibold mb-2">Recommendations</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 bg-purple-50 p-4 rounded-lg">
                    {selectedIncident.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notification Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-3 rounded-lg ${selectedIncident.governmentNotified ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {selectedIncident.governmentNotified ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-semibold">
                      Government {selectedIncident.governmentNotified ? 'Notified' : 'Not Notified'}
                    </span>
                  </div>
                  {selectedIncident.governmentNotificationDate && (
                    <div className="text-xs text-gray-600 mt-1">
                      {new Date(selectedIncident.governmentNotificationDate).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${selectedIncident.familyNotified ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {selectedIncident.familyNotified ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-semibold">
                      Family {selectedIncident.familyNotified ? 'Notified' : 'Not Notified'}
                    </span>
                  </div>
                  {selectedIncident.familyNotificationDate && (
                    <div className="text-xs text-gray-600 mt-1">
                      {new Date(selectedIncident.familyNotificationDate).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Incident Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-red-800 flex items-center gap-2">
                <Plus className="w-6 h-6 text-red-600" />
                Report SIRS Incident
              </h2>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Incident Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
                    <option>Physical Abuse</option>
                    <option>Neglect</option>
                    <option>Exploitation</option>
                    <option>Unreasonable Use of Force</option>
                    <option>Sexual Assault</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
                    <option>Priority 1 (24 hours)</option>
                    <option>Priority 2 (30 days)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 h-32"
                  placeholder="Provide detailed description of the incident..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    showToast('SIRS incident reported successfully', 'success');
                    setShowNewModal(false);
                    loadData();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                >
                  Report Incident
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-red-600" />
                Export SIRS Data
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const csv = generateCSV();
                  downloadFile(csv, `sirs-${selectedFacility}.csv`, 'text/csv');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  const json = JSON.stringify(incidents, null, 2);
                  downloadFile(json, `sirs-${selectedFacility}.json`, 'application/json');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function generateCSV() {
    const headers = ['ID', 'Type', 'Resident', 'Priority', 'Severity', 'Status', 'Report Date', 'Due Date'];
    const rows = incidents.map(i => [
      i.incidentId,
      i.incidentType,
      i.residentName,
      i.reportType,
      i.severity,
      i.status,
      i.reportDate,
      i.dueDate || 'N/A'
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export default SIRSDashboard;
